import { continueAfterLogin, deviseSignIn } from '@polydice/icook-devise-unify';
import { triggerToast } from '@polydice/ui/dist/Toast';
import classNames from 'classnames';
import { webviewTrack } from 'components/webviewTrack';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  getAuthToken,
  getDateStatus,
  getFareDescription,
  scrollTo,
  storageKey
} from 'reactjs/components/utility';
import useScrollResizeHook from 'reactjs/utils/useScrollResize';
import { ProductWithSkusProps } from '../../propTypes/product';
import ProductAction from './ProductAction';
import ProductCoupon from './ProductCoupon';
import ProductFundraising from './ProductFundraising';
import {
  BUYING_STATE,
  WATCHING_STATE,
  getLowestPrice,
  isProductSkusContainOrderSkus
} from './ProductHelper';
import ProductIntro from './ProductIntro';
import ProductOrder from './ProductOrder';
import styles from './ProductSidebar.module.scss';

const ProductSidebar = ({
  user,
  product,
  searchMode,
  fundraisingMode,
  fundraisingCount,
  fundraisingTotal,
  promotion,
  coupon,
  favoriteEndpoint,
  orderEndpoint,
  isFavorite,
  isHybrid,
  darkMode,
  isCool3c
}) => {
  const {
    id,
    name: productName,
    skus,
    accept_credit_card_installment,
    credit_card_installment_period,
    selling_program_id,
    started_at,
    ended_at
  } = product;

  const continueAfterLoginKey = 'buy_product';
  const dateStatus = getDateStatus(started_at, ended_at);
  const formRef = useRef(null);
  const tnlAuthFormRef = useRef(null);
  const [status, setStatus] = useState(WATCHING_STATE);
  const [orderProducts, setOrderProducts] = useState({});

  const target = useRef(null);
  useScrollResizeHook(target, styles.productSidebarPanelFixedSide, true);

  useEffect(() => {
    const triggerUpdate = (event) => {
      const {
        detail: { selectedProduct, selectedQuantity, currentItemInStock }
      } = event;
      const { id, name, price, feature } = selectedProduct;
      setOrderProducts((orderProducts) => {
        const alreadyExist = {}.hasOwnProperty.call(orderProducts, id);
        const quantityToAdd = (nextQuantity) =>
          nextQuantity >= currentItemInStock
            ? currentItemInStock
            : nextQuantity;
        return {
          ...orderProducts,
          [id]: {
            sku_id: id,
            price,
            quantity: alreadyExist
              ? quantityToAdd(orderProducts[id].quantity + selectedQuantity)
              : selectedQuantity
          }
        };
      });
      if (window?.daex) {
        window.daex('send', 'event', {
          name: 'add_to_cart'
        });
      }
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          add: {
            actionField: {
              list: productName
            },
            products: [
              {
                name,
                id,
                price,
                brand: productName,
                variant: feature,
                quantity: selectedQuantity
              }
            ]
          },
          currency: 'TWD',
          value: price,
          items: [
            {
              item_id: id,
              item_name: name,
              item_brand: productName,
              item_variant: feature,
              quantity: selectedQuantity,
              price
            }
          ]
        }
      });
      setStatus(BUYING_STATE);
      triggerToast({
        className: styles.productSidebarToast,
        text: '已加入購買',
        closable: true
      });
    };
    window.addEventListener('Buy', triggerUpdate);
    return () => window.removeEventListener('Buy', triggerUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(() => {
    if (dateStatus === 'expired') {
      window.alert('商品已完售，下次請早喔。');
    } else {
      formRef.current.submit();
    }
  }, [dateStatus]);

  const handleLogin = () => {
    const trackName = 'member_feature_requirement_label_clicked';
    const trackParameters = JSON.stringify({
      where: 'make_order',
      label: 'log_in'
    });
    webviewTrack(trackName, trackParameters);
    if (isCool3c) {
      tnlAuthFormRef.current.submit();
    } else {
      deviseSignIn({
        entry: 'market_product_pay',
        redirect: window.location.href,
        continueAfterLogin: continueAfterLoginKey,
        in_web: 1
      });
    }
  };

  const [submitImmediately, setSubmitImmediately] = useState(false);

  useEffect(() => {
    const skuInSessionStorage = sessionStorage.getItem(storageKey);
    if (skuInSessionStorage) {
      const selectedSkus = JSON.parse(skuInSessionStorage);
      if (continueAfterLogin(continueAfterLoginKey)) {
        setOrderProducts(selectedSkus);
        setSubmitImmediately(true);
      } else {
        if (!isProductSkusContainOrderSkus(product.skus, selectedSkus)) {
          sessionStorage.removeItem(storageKey);
        } else {
          setOrderProducts(selectedSkus);
          setStatus(BUYING_STATE);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submitImmediately) {
      handleSubmit();
    }
  }, [submitImmediately, handleSubmit]);

  useEffect(() => {
    if (Object.keys(orderProducts).length !== 0) {
      sessionStorage.setItem(storageKey, JSON.stringify(orderProducts));
    }
  }, [orderProducts]);

  useEffect(() => {
    if (window.location.hash === '#buy-options-start') {
      const element = document.getElementById('buy-options-start');
      scrollTo(element);
      setTimeout(() => scrollTo(element), 600);
    }
  }, []);

  const [authToken, setAuthToken] = useState('');
  useEffect(() => {
    setAuthToken(getAuthToken());
  }, []);

  // 銷售方案 一般 = 1, 募資 = 2
  const showDetail =
    selling_program_id === 1 ||
    (selling_program_id === 2 && dateStatus !== 'future');

  return (
    <section
      className={classNames(styles.productSidebar, {
        'dark-mode': darkMode
      })}
    >
      <ProductIntro product={product} darkMode={darkMode} isCool3c={isCool3c} />
      <div>
        <section
          className={classNames(styles.productSidebarPanel, {
            'dark-mode gray2-bgc no-border mobile-black': darkMode
          })}
          ref={target}
        >
          {!searchMode && fundraisingMode && (
            <ProductFundraising
              product={product}
              fundraisingCount={fundraisingCount}
              fundraisingTotal={fundraisingTotal}
              status={status}
              darkMode={darkMode}
            />
          )}
          {dateStatus !== 'expired' &&
            !searchMode &&
            !fundraisingMode &&
            skus.length !== 0 && (
              <Fragment>
                <span
                  className={classNames(styles.productSidebarPanelLowerPrice, {
                    'dark-mode': darkMode
                  })}
                >
                  最低價
                </span>
                <span
                  className={classNames(styles.productSidebarPanelPrice, {
                    'dark-mode moblie-blue-color': darkMode
                  })}
                >
                  NT$ {getLowestPrice(product)}
                  <small className={styles.productSidebarPanelPriceText}>
                    起
                  </small>
                </span>
              </Fragment>
            )}
          {status === WATCHING_STATE && (
            <ProductAction
              searchMode={searchMode}
              product={product}
              favoriteEndpoint={favoriteEndpoint}
              isFavorite={isFavorite}
              user={user}
              coupon={coupon}
              isHybrid={isHybrid}
              fundraisingMode={fundraisingMode}
              darkMode={darkMode}
            />
          )}
          {status === BUYING_STATE && (
            <>
              <ProductOrder
                user={user}
                product={product}
                coupon={coupon}
                orderProducts={orderProducts}
                onClick={user ? handleSubmit : handleLogin}
                darkMode={darkMode}
              />
              <form
                acceptCharset="UTF-8"
                action="/auth/tnl"
                method="POST"
                ref={tnlAuthFormRef}
              >
                <input
                  type="hidden"
                  defaultValue={authToken}
                  name="authenticity_token"
                />
              </form>
            </>
          )}
          {showDetail && (
            <>
              {promotion && (
                <div
                  className={classNames(
                    styles.productSidebarPanelItem,
                    styles.productSidebarPanelItemPromotion,
                    {
                      'dark-mode gray2-bgc gray-border mobile-black': darkMode
                    }
                  )}
                >
                  <i
                    className={classNames(
                      'icon-megaphoned-regular',
                      styles.productSidebarPanelItemIcon,
                      {
                        'dark-mode gray2-bgc blue-color mobile-black': darkMode
                      }
                    )}
                  />
                  <span
                    className={classNames(styles.productSidebarPanelItemText, {
                      'dark-mode gray2-bgc blue-color mobile-black': darkMode
                    })}
                  >
                    {promotion}
                  </span>
                </div>
              )}
              {coupon && (
                <div
                  className={classNames(styles.productSidebarPanelItem, {
                    'dark-mode gray2-bgc gray-border mobile-black': darkMode
                  })}
                >
                  <i
                    className={classNames(
                      'icon-coupon-regular',
                      styles.productSidebarPanelItemIcon,
                      { 'dark-mode gray2-bgc': darkMode }
                    )}
                  />
                  <ProductCoupon
                    user={user}
                    coupon={coupon}
                    darkMode={darkMode}
                  />
                </div>
              )}
              {accept_credit_card_installment && (
                <div
                  className={classNames(styles.productSidebarPanelItem, {
                    'dark-mode gray2-bgc gray-border mobile-black': darkMode
                  })}
                >
                  <i
                    className={classNames(
                      'icon-credit-card-regular',
                      styles.productSidebarPanelItemIcon,
                      { 'dark-mode gray2-bgc mobile-black': darkMode }
                    )}
                  />
                  <span
                    className={classNames(styles.productSidebarPanelItemText, {
                      'dark-mode gray2-bgc mobile-black': darkMode
                    })}
                  >
                    可分 {credit_card_installment_period.join(' / ')} 期零利率
                  </span>
                </div>
              )}
              <div
                className={classNames(styles.productSidebarPanelItem, {
                  'dark-mode gray2-bgc gray-border mobile-black': darkMode
                })}
              >
                <i
                  className={classNames(
                    'icon-shipping-regular',
                    styles.productSidebarPanelItemIcon,
                    { 'dark-mode gray2-bgc mobile-black': darkMode }
                  )}
                />
                <span
                  className={classNames(styles.productSidebarPanelItemText, {
                    'dark-mode gray2-bgc mobile-black': darkMode
                  })}
                >
                  {getFareDescription(product)}
                </span>
              </div>
            </>
          )}
        </section>
      </div>
      <form
        acceptCharset="UTF-8"
        action={orderEndpoint}
        method="POST"
        ref={formRef}
      >
        <input type="hidden" defaultValue={id} name="order[product_id]" />
        <input
          type="hidden"
          defaultValue={authToken}
          name="authenticity_token"
        />
        {Object.keys(orderProducts).map((skuId, index) => {
          const sku = orderProducts[skuId];
          return (
            <fieldset key={sku.sku_id}>
              <input
                type="hidden"
                name={`order[order_skus_attributes][${index}][sku_id]`}
                value={sku.sku_id}
              />
              <input
                type="hidden"
                name={`order[order_skus_attributes][${index}][quantity]`}
                value={sku.quantity}
              />
            </fieldset>
          );
        })}
      </form>
    </section>
  );
};

ProductSidebar.propTypes = {
  user: PropTypes.shape({}),
  product: ProductWithSkusProps.isRequired,
  searchMode: PropTypes.shape({}),
  fundraisingMode: PropTypes.bool.isRequired,
  fundraisingCount: PropTypes.number.isRequired,
  fundraisingTotal: PropTypes.number.isRequired,
  promotion: PropTypes.string,
  coupon: PropTypes.shape({}),
  favoriteEndpoint: PropTypes.string.isRequired,
  orderEndpoint: PropTypes.string.isRequired,
  isFavorite: PropTypes.bool.isRequired,
  isHybrid: PropTypes.bool.isRequired
};

ProductSidebar.defaultProps = {
  user: null,
  coupon: null,
  promotion: ''
};

export default ProductSidebar;
